import type { Lesson } from '@yuan-math/curriculum';
import { figure, insight, lesson, list, math, practice, steps, table, text, tip, warning } from './authoring.ts';

/**
 * The Grade 12 calculus pathway, from continuity to the fundamental theorem.
 *
 * Two lessons here carry most of the weight, and for opposite reasons.
 *
 * `continuity` is first because it is the vocabulary the rest of the course
 * assumes and never states. `limits` introduced the notation on a function with
 * a hole — one missing point, and the limit sails past it — which is the
 * kindest possible first case and leaves a student with exactly one model of
 * what can go wrong. Three things can go wrong, they look nothing alike, and
 * only one of them is a hole. Every later lesson says "assume f is continuous"
 * and means "none of these three".
 *
 * `fundamental-theorem` is last because it is the only genuine surprise in the
 * course, and the surprise is destroyed by teaching it early. Area under a
 * curve and undoing a derivative are not obviously related — they were not
 * obviously related to anyone for two thousand years — so the two are built
 * separately, `integrals` computes an area the hard way with rectangles, and
 * only then does the theorem arrive and make that labour unnecessary. A course
 * that opens the integral chapter with "to find an area, antidifferentiate"
 * has stated the punchline as a definition, and the student never learns that
 * anything was proved.
 *
 * `derivatives` derives the power rule rather than announcing it. The
 * difference quotient is expanded for x² and for x³, the pattern is read off
 * two rows of a table, and only then is the rule stated for a general n. This
 * costs a page and buys the thing the whole pathway depends on: that "bring the
 * power down" is a consequence rather than a convention. A student who meets it
 * as a convention cannot differentiate anything the rule does not literally
 * cover, and cannot say why the derivative of a constant is zero.
 *
 * `derivative-rules` leads with the counterexample rather than with the rule.
 * (uv)′ = u′v′ is the guess every student makes, it is wrong, and x · x settles
 * it in one line: the product is x², whose derivative is 2x, and u′v′ is 1.
 * Stating the product rule before that objection has been raised teaches a
 * formula defending against an attack the student has not made yet.
 *
 * `optimization` spends its warnings on the last step rather than the calculus.
 * Finding x = 25 is not an answer to "what is the largest area", and the error
 * is not mathematical — it is answering the question the algebra ended on
 * instead of the question that was asked. Every worked optimisation in this
 * lesson finishes by rereading the prompt.
 */
export const CALCULUS_LESSONS: Lesson[] = [
  lesson({
    id: 'continuity-01',
    unitId: 'unit-limits',
    conceptId: 'continuity',
    title: 'Continuity and One-Sided Limits',
    summary: 'The three ways a limit can fail, and the functions where none of them do.',
    objectives: [
      'Find a left-hand and a right-hand limit, and say when the two-sided limit exists',
      'Test a function for continuity at a point using the three conditions',
      'Describe a limit that is infinite, and a limit taken as x grows without bound',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Every limit so far has existed',
        blocks: [
          text('The limits lesson worked one kind of example: a function with a single point missing, where the curve either side agrees about where it is heading. The limit existed; only the value was absent.'),
          text('That is the gentlest thing that can happen, and it leaves a false impression. Here are three functions and one question — what is the limit as x approaches the marked point?'),
          table(['Function', 'At', 'What happens'], [
            ['(x² − 4)/(x − 2)', 'x = 2', 'Both sides head for 4. One point is missing.'],
            ['a price that jumps at midnight', 'midnight', 'One side heads for £3, the other for £5.'],
            ['1/x²', 'x = 0', 'Both sides climb without ever settling.'],
          ]),
          insight('Only the first has a limit. The other two fail, they fail differently, and "the limit does not exist" is the same four words for two completely different situations. This lesson is the vocabulary for telling them apart.'),
        ],
      },
      intuition: {
        title: 'Approach from one side at a time',
        blocks: [
          text('Split the question. Approaching a from below is the **left-hand limit**; from above, the **right-hand limit**:'),
          math('\\lim_{x \\to a^{-}} f(x) \\qquad \\text{and} \\qquad \\lim_{x \\to a^{+}} f(x)'),
          insight('The small sign is a direction, not a subtraction. x → 2⁻ means x is 1.9, then 1.99, then 1.999 — creeping up on 2 from the left. It has nothing to do with negative numbers, and −2 is not involved.'),
          text('The two-sided limit is what the last lesson called "the" limit, and it exists exactly when the two one-sided limits agree:'),
          math('\\lim_{x \\to a} f(x) = L \\iff \\lim_{x \\to a^{-}} f(x) = \\lim_{x \\to a^{+}} f(x) = L'),
          text('That is the whole of the second row of the table. A price of £3 before midnight and £5 after has both one-sided limits, they are perfectly well defined, and they disagree — so there is no limit at midnight. This is a **jump discontinuity**.'),
          text('**Continuity.** A function is continuous at a when three things hold, in this order:'),
          list([
            'f(a) exists — the function has a value there at all,',
            'the limit as x → a exists — both sides agree about where it is heading, and',
            'the two are equal — where it heads is where it lands.',
          ], true),
          insight('Each condition rules out one row of the table. A hole fails the first, a jump fails the second, and a function defined at a point but drawn heading somewhere else fails the third. Informally: continuous means you can draw it without lifting the pen, and the three conditions are what that sentence means when the drawing is not available.'),
          warning('"Undefined at a" and "no limit at a" are different claims, and neither implies the other. (x² − 4)/(x − 2) is undefined at 2 and has a limit there; a piecewise function can be perfectly well defined at a jump and have none. Check the two separately.', 'Undefined is not the same as no limit'),
          text('**Infinite limits.** The third row is neither of the above. As x → 0, 1/x² does not approach a number and does not jump — it grows past every bound:'),
          math('\\lim_{x \\to 0} \\frac{1}{x^{2}} = \\infty'),
          warning('That equals sign is a description, not a value. ∞ is not a number the function reaches, and it cannot be substituted, added to or cancelled. Writing = ∞ says precisely "this limit fails, and it fails by growing" — which is more information than "does not exist", and is the reason the notation is allowed at all.', '∞ is a behaviour, not an answer'),
          text('An infinite limit is what a **vertical asymptote** is. It is the same picture Algebra II drew for rational functions, now with the notation to say it.'),
          text('**Limits at infinity.** Turn the question round and ask not what happens near a point but what happens far from every point:'),
          math('\\lim_{x \\to \\infty} \\frac{3x^{2} + 5x}{2x^{2} - 1} = \\frac{3}{2}'),
          tip('Divide top and bottom by the highest power present and every term with an x underneath vanishes, leaving the ratio of the leading coefficients. This limit is a horizontal asymptote — the same object, again, with notation.'),
          warning('Compare the degrees before doing any work. Top degree smaller than bottom gives 0; equal gives the ratio of leading coefficients; larger gives no finite limit at all. Reading the answer off the constant terms instead is the standard error, and the constant term is the one part of a polynomial that stops mattering as x grows.', 'The leading term decides'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'piecewise-graph',
              pieces: [
                { slope: 1, intercept: 1, from: -3, to: 2, openEnd: true },
                { slope: -1, intercept: 7, from: 2, to: 6 },
              ],
              min: -3,
              max: 7,
            },
            'A jump. Coming from the left the values head for 3; from the right they head for 5. Both one-sided limits exist and are perfectly definite, they disagree, and so there is no limit at x = 2 — even though the filled dot shows the function is defined there.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'reciprocal',
              asymptotes: { vertical: [0], horizontal: [0] },
              min: -5,
              max: 5,
              label: '1/x',
            },
            'Two failures in one picture. At x = 0 the branches run off in opposite directions — an infinite limit, and the left and right sides do not even fail the same way. Far out in either direction the curve settles onto the horizontal axis, which is a limit at infinity, and that one exists.',
          ),
          tip('The dashed lines are not part of the graph. An asymptote is a description of where the curve goes, and drawing it is a convenience — the function has no value on it anywhere.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For what value of k is this function continuous at x = 3?'),
          math('f(x) = \\begin{cases} 2x + k & x < 3 \\\\ x^{2} & x \\geq 3 \\end{cases}'),
          steps([
            { label: 'The left-hand limit, from the top branch.', latex: '\\lim_{x \\to 3^{-}} f(x) = 2(3) + k = 6 + k' },
            { label: 'The right-hand limit, from the bottom branch.', latex: '\\lim_{x \\to 3^{+}} f(x) = 3^{2} = 9' },
            { label: 'The value at 3 — the second branch owns x = 3.', latex: 'f(3) = 9', note: 'The ≥ decides this, and it is why the branch was written with ≥ rather than >.' },
            { label: 'Continuity needs all three equal.', latex: '6 + k = 9' },
            { label: 'Solve.', latex: 'k = 3' },
          ]),
          insight('With k = 3 the two branches meet at the same height and the graph is unbroken. With any other k there is a jump — the function still has a value at 3, and still has both one-sided limits, and is still discontinuous. Being defined everywhere is not continuity.'),
          warning('Read which branch owns the endpoint before computing f(3). Here the ≥ puts x = 3 on the x² branch; had it been written x > 3, the value at 3 would have come from 2x + k and the third condition would be a different equation.', 'Which branch owns the point'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('continuity', 4)] },
      independent: { title: 'On your own', blocks: [practice('continuity', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('continuity', 3, 'One-sided limits, the constant that closes a gap, and behaviour far from the origin.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'x → a⁻ means approaching from below; the sign is a direction, not a minus.',
            'The two-sided limit exists exactly when both one-sided limits exist and agree.',
            'Continuous at a means: f(a) exists, the limit exists, and they are equal.',
            'A limit can fail three ways — a hole, a jump, or growing without bound.',
            '"= ∞" describes how a limit fails; ∞ is never a value to substitute.',
            'For a limit at infinity, compare the degrees: the leading terms decide.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'derivatives-01',
    unitId: 'unit-derivatives',
    conceptId: 'derivatives',
    title: 'The Derivative',
    summary: 'The slope at every point at once, as a function in its own right.',
    objectives: [
      'State the derivative as a limit of the difference quotient',
      'Derive and then apply the power rule',
      'Differentiate any polynomial, and evaluate the result at a point',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Doing it once instead of once each',
        blocks: [
          text('The last precalculus lesson found the slope of y = x² at x = 3. It took six lines and gave one number: 6.'),
          text('Now find the slope at x = 5. Six more lines, and 10. At x = 7: six more, and 14. This is unbearable, and it is also unnecessary — the six lines are identical every time except for the number.'),
          insight('So do not put a number in. Leave the point as x and run the same calculation once. What comes out is not a slope; it is a *function* whose output at any x is the slope there. That function is the **derivative**, and everything in this course is built on it.'),
        ],
      },
      intuition: {
        title: 'The same limit, with the point left as a letter',
        blocks: [
          text('Here is the difference quotient again, with x where a used to be:'),
          math("f'(x) = \\lim_{h \\to 0} \\frac{f(x + h) - f(x)}{h}"),
          text('Run it on f(x) = x², expanding as before and cancelling the h:'),
          steps([
            { label: 'Write the quotient.', latex: '\\frac{(x + h)^{2} - x^{2}}{h}' },
            { label: 'Expand the square.', latex: '\\frac{x^{2} + 2xh + h^{2} - x^{2}}{h} = \\frac{2xh + h^{2}}{h}' },
            { label: 'Factor out h and cancel — legal, h is not zero.', latex: '2x + h' },
            { label: 'Let h shrink to nothing.', latex: "f'(x) = 2x" },
          ]),
          text('One calculation, every point. The slope at 3 is 6, at 5 is 10, at 7 is 14 — three answers read off, with no further work.'),
          text('Do the same to x³ and the numerator expands to 3x²h + 3xh² + h³, which cancels down to 3x² + 3xh + h², and the limit is 3x². Two results are enough to see the pattern:'),
          table(['f(x)', 'f′(x)'], [
            ['x', '1'],
            ['x²', '2x'],
            ['x³', '3x²'],
            ['x⁴', '4x³'],
            ['xⁿ', 'n·xⁿ⁻¹'],
          ]),
          insight('The **power rule**: bring the power down as a multiplier and reduce it by one. It is worth stressing that the last row is a *conclusion*. It was not decreed; it fell out of expanding (x + h)ⁿ, where the second term of the binomial expansion is the only one that survives the cancelling. The binomial theorem and this rule are the same fact.'),
          text('Three more rules, all of which say that differentiation does not disturb the arithmetic around it:'),
          math("(c)' = 0, \\qquad (c \\cdot f)' = c \\cdot f', \\qquad (f + g)' = f' + g'"),
          warning('The derivative of a constant is 0, not the constant. y = 7 is a horizontal line; its slope is zero everywhere. This is not a special case to memorise — it is the power rule with n = 0, since 7 = 7x⁰ and the rule brings down that 0.', 'A constant has no slope'),
          warning('The power rule applies to a power of x, not to a power of anything else and not to a product of two things. (x² + 1)⁵ is not 5(x² + 1)⁴, and x²·x³ is not 2x·3x². The next lesson is entirely about what to do instead; until then, expand first.', 'Only on a plain power'),
          text('**Notation.** Three ways of writing the same object, and you will meet all three:'),
          math("f'(x) \\qquad \\frac{dy}{dx} \\qquad \\frac{d}{dx}\\big[f(x)\\big]"),
          tip('dy/dx is read "dee y by dee x" and is one symbol, not a fraction — the d is not a quantity and cannot be cancelled. It is written that way because it remembers where it came from: Δy/Δx, with both differences shrunk to nothing.'),
          text('One last thing the definition tells you. If a function has a break or a sharp corner, no tangent exists there, and the limit defining the derivative fails:'),
          insight('Differentiable implies continuous, and the reverse is false. |x| is continuous at 0 — you can draw it without lifting the pen — and has no derivative there, because the slope coming in is −1 and the slope going out is +1. A corner is continuous and not smooth.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              lines: [{ slope: 4, intercept: -4, label: 'tangent at x = 2' }],
              points: [{ x: 2, y: 4, label: '(2, 4)' }],
              min: -3,
              max: 6,
              label: 'y = x²',
            },
            'The tangent to y = x² at x = 2 has slope 4 — and f′(x) = 2x gives 2(2) = 4 without any drawing. The derivative is a machine for producing this line at any point on the curve.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'absolute',
              points: [{ x: 0, y: 0, label: 'no tangent here' }],
              min: -4,
              max: 4,
              label: 'y = |x|',
            },
            'Continuous everywhere, differentiable everywhere except one point. At the corner the slope from the left is −1 and from the right is +1; there is no single line touching the graph there, so the two-sided limit in the definition does not exist.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For f(x) = x³ − 5x² + 2x − 7, find f′(x) and then the slope of the curve at x = 2.'),
          steps([
            { label: 'Differentiate term by term — the sum rule allows it.', latex: "f'(x) = 3x^{2} - 10x + 2 - 0" },
            { label: 'Tidy: the constant contributed nothing.', latex: "f'(x) = 3x^{2} - 10x + 2" },
            { label: 'Substitute x = 2.', latex: "f'(2) = 3(4) - 10(2) + 2" },
            { label: 'Arithmetic.', latex: '12 - 20 + 2 = -6' },
          ]),
          insight('The negative answer is information, not an error: at x = 2 the curve is falling, and falling at a rate of 6 units of height per unit across. A sign is the first thing to read off a derivative and the last thing worth throwing away.'),
          warning('f′(x) and f′(2) are different objects, and the question asks for both. The first is a function — the whole rule. The second is one number obtained by substituting into it. Handing back 3x² − 10x + 2 when a slope at a point was asked for is the commonest way to lose this mark.', 'The function and its value'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('derivatives', 4)] },
      independent: { title: 'On your own', blocks: [practice('derivatives', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('derivatives', 3, 'Derivatives from the definition, and slopes at a named point.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'f′(x) = lim(h→0) [f(x + h) − f(x)] / h — the difference quotient, with the point left as x.',
            'Power rule: the derivative of xⁿ is n·xⁿ⁻¹.',
            'The derivative of a constant is 0; constants multiplying a function come along unchanged.',
            'Differentiate a sum term by term.',
            'The power rule works on a plain power of x and nothing else.',
            'Differentiable implies continuous; a corner shows the converse is false.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'derivative-rules-01',
    unitId: 'unit-derivatives',
    conceptId: 'derivative-rules',
    title: 'Product, Quotient and Chain',
    summary: 'Differentiating things built out of other things — and why none of the three rules is the obvious guess.',
    objectives: [
      'Differentiate a product and a quotient of two functions',
      'Differentiate a composition using the chain rule',
      'Recognise which rule a given expression needs',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A guess, and a one-line refutation',
        blocks: [
          text('You can differentiate any polynomial. You cannot yet differentiate these:'),
          math('x^{2}(2x + 5)^{3}, \\qquad \\frac{2x + 1}{x - 3}, \\qquad (3x + 1)^{10}'),
          text('The first two could be expanded, with effort. The third could not — nobody expands a tenth power by hand — and in any case expanding is not a method, it is an evasion.'),
          text('The natural guess is that differentiation goes through a product the way it goes through a sum: differentiate each part. Test it on the simplest product there is, x times x:'),
          math("(x \\cdot x)' = (x^{2})' = 2x, \\qquad \\text{but} \\qquad x' \\cdot x' = 1 \\cdot 1 = 1"),
          insight('2x and 1 are not the same. The guess is dead, and it took one line to kill it — which is the right order: know why a rule is needed before being handed one.'),
        ],
      },
      intuition: {
        title: 'Three rules, each with its own trap',
        blocks: [
          text('**The product rule.** Differentiate one factor at a time and add the two results:'),
          math("(uv)' = u'v + uv'"),
          text('Check it against the counterexample: u = v = x gives 1·x + x·1 = 2x, which is right.'),
          tip('The sentence to say while writing it is "derivative of the first times the second, plus the first times derivative of the second". Two terms, each containing one differentiated factor and one untouched one — if a term of yours has both factors differentiated, or neither, it is wrong.'),
          text('**The quotient rule.** Same shape, one subtraction and a denominator:'),
          math("\\left(\\frac{u}{v}\\right)' = \\frac{u'v - uv'}{v^{2}}"),
          warning('The numerator is a subtraction, so the order matters and cannot be swapped. u′v comes first — the derivative of the *top* times the bottom. Reversing the two terms negates the whole answer, and negating a slope claims a curve is rising where it falls.', 'The top is differentiated first'),
          warning('The v² is the original denominator squared, not the derivative of anything. It is also the reason a quotient rule answer is almost never worth expanding: leaving (x − 3)² intact keeps visible the one place the derivative does not exist.', 'Square the bottom you started with'),
          text('**The chain rule.** This is the one that matters most, and the one most often left half-applied. For a function inside a function:'),
          math("\\big(f(g(x))\\big)' = f'(g(x)) \\cdot g'(x)"),
          insight('Differentiate the outside, leaving the inside alone, then multiply by the derivative of the inside. The second factor is the whole content of the rule: a chain of change. If the inside changes three times as fast as x, the outside changes three times as fast as it otherwise would, and the 3 has to appear.'),
          text('So (3x + 1)¹⁰ differentiates to 10(3x + 1)⁹ · 3 = 30(3x + 1)⁹. The bracket is untouched; the 3 is where the chain rule earns its name.'),
          warning('Dropping the derivative of the inside is the single commonest error in this course. It produces an answer that looks completely right — same shape, same power, correct-looking bracket — and is wrong by a constant factor. Check it at a value: at x = 0, (3x + 1)¹⁰ is climbing at 30, not 10.', 'The inside derivative is not optional'),
          text('Choosing between the three is a question about the *outermost* operation, and asking it in the right order settles most of the difficulty:'),
          table(['If the expression is…', 'Use'], [
            ['a sum of terms', 'differentiate term by term'],
            ['two things multiplied', 'the product rule'],
            ['one thing over another', 'the quotient rule'],
            ['something raised to a power, or a function of a bracket', 'the chain rule'],
          ]),
          tip('The rules combine, and a hard expression needs two or three of them nested. x²(2x + 5)³ is a product whose second factor needs the chain rule; do the outer rule first and let each piece ask for what it needs.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              transform: { b: 2 },
              showParent: true,
              min: -3,
              max: 6,
              label: 'y = (2x)²',
            },
            'The chain rule, drawn. The dashed curve is y = x²; the solid one is y = (2x)², the same curve with the inside running twice as fast. At every height the solid curve is steeper — by a factor of exactly 2, which is the derivative of the inside and the factor the chain rule supplies.',
          ),
          tip('This is why the chain-rule factor cannot be dropped as a detail. It is not a correction to the answer; it is the difference between two visibly different curves.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Differentiate y = (3x² + 1)⁴.'),
          steps([
            { label: 'Identify the outside and the inside.', latex: '\\text{outside: } u^{4}, \\quad \\text{inside: } u = 3x^{2} + 1' },
            { label: 'Differentiate the outside, inside left alone.', latex: '4(3x^{2} + 1)^{3}' },
            { label: 'Differentiate the inside.', latex: "\\frac{du}{dx} = 6x" },
            { label: 'Multiply the two.', latex: '4(3x^{2} + 1)^{3} \\cdot 6x' },
            { label: 'Tidy the constants to the front.', latex: "\\frac{dy}{dx} = 24x(3x^{2} + 1)^{3}" },
          ]),
          insight('Two checks, both cheap. At x = 0 the inside is at its minimum, so the curve should be flat there — and 24x is zero at x = 0, so it is. And the power dropped from 4 to 3 exactly once: a bracket whose power did not fall has not been differentiated, and one that fell twice has been differentiated twice.'),
          warning('Do not expand the bracket to check. (3x² + 1)⁴ is a degree-eight polynomial, and expanding it to verify a two-line answer is how a correct answer gets abandoned. Substitute a number into both instead — at x = 1 the derivative is 24 · 4³ = 1536, and a numerical difference quotient on the original agrees.', 'Check by substitution, not expansion'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('derivative-rules', 4)] },
      independent: { title: 'On your own', blocks: [practice('derivative-rules', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('derivative-rules', 3, 'Choosing the rule, and the rules combined.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            '(uv)′ = u′v + uv′ — two terms, each with exactly one factor differentiated.',
            '(u/v)′ = (u′v − uv′)/v² — the top is differentiated first, and the order cannot be swapped.',
            'Chain rule: differentiate the outside, leave the inside alone, multiply by the inside’s derivative.',
            'The inside derivative is the rule; forgetting it gives a wrong answer that looks right.',
            'Pick the rule by the outermost operation, then let each piece ask for what it needs.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'implicit-differentiation-01',
    unitId: 'unit-derivatives',
    conceptId: 'implicit-differentiation',
    title: 'Implicit Differentiation and Related Rates',
    summary: 'Differentiating an equation nobody solved for y, and the moving problems that turns out to answer.',
    objectives: [
      'Differentiate an equation implicitly and solve for dy/dx',
      'Find the slope of a curve that is not a function',
      'Set up and solve a related-rates problem',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A curve that is not a function',
        blocks: [
          text('Find the slope of the circle x² + y² = 25 at the point (3, 4).'),
          text('Everything so far has needed y written in terms of x, and this cannot be — not as one function. Solving gives y = ±√(25 − x²), two functions, and a sign to keep track of by hand for the rest of the problem.'),
          insight('There is a way to differentiate the equation exactly as it stands, without solving for anything. It costs one idea: y is a function of x even when nobody has said which one, so differentiating y² needs the chain rule.'),
        ],
      },
      intuition: {
        title: 'y is a function of x, so the chain rule applies to it',
        blocks: [
          text('Differentiate both sides with respect to x, term by term. x² gives 2x as usual. y² is where the work is:'),
          math("\\frac{d}{dx}\\big(y^{2}\\big) = 2y \\cdot \\frac{dy}{dx}"),
          insight('That is the chain rule and nothing else. y² is something-squared where the something is a function of x, so the outside gives 2y and the inside gives dy/dx. The factor is not a convention of this topic; it is the same factor that turns (3x + 1)¹⁰ into 30(3x + 1)⁹.'),
          text('So the whole equation differentiates, and then rearranges:'),
          steps([
            { label: 'Differentiate both sides with respect to x.', latex: '2x + 2y\\frac{dy}{dx} = 0' },
            { label: 'Move the x term across.', latex: '2y\\frac{dy}{dx} = -2x' },
            { label: 'Divide.', latex: '\\frac{dy}{dx} = -\\frac{x}{y}' },
          ]),
          text('At (3, 4) that is −3/4. The check is geometry: the radius to (3, 4) has slope 4/3, and −3/4 is its negative reciprocal, so the tangent is perpendicular to the radius — which is a circle theorem from Grade 10, arrived at by calculus.'),
          warning('Every y differentiated leaves a dy/dx behind. Omitting it is the defining error of this topic, and it is quiet: the equation still looks balanced, still solves, and gives an answer that is simply wrong. Before rearranging, count the y terms and count the dy/dx factors.', 'Every y leaves a trace'),
          insight('Notice that the answer contains both x and y. That is not an unfinished calculation — it is correct and unavoidable. A circle has two points at each x, with opposite slopes, and a formula that mentioned only x could not tell them apart.'),
          text('**Related rates.** Now change one letter and the same calculation answers a different kind of question. Suppose x and y both vary with time. Differentiate with respect to t instead of x:'),
          math('2x\\frac{dx}{dt} + 2y\\frac{dy}{dt} = 0'),
          insight('Nothing new happened. Every variable is a function of t, so every one of them leaves a rate behind, exactly as y left dy/dx. Related rates is implicit differentiation with t as the independent variable, and it is worth seeing the two written one under the other before any word problem arrives.'),
          text('The method, once the equation relating the quantities is written down:'),
          list([
            'Write the relationship between the quantities — geometry, usually.',
            'Differentiate both sides with respect to t, chain rule on every variable.',
            'Substitute the values at the instant asked about, including the known rate.',
            'Solve for the rate you want, and read its sign.',
          ], true),
          warning('Substitute the instant’s numbers *after* differentiating, never before. A quantity replaced by a constant has derivative zero, so putting x = 6 into the relationship first deletes dx/dt — the very rate the problem gave you — and the equation collapses to something with no solution.', 'Differentiate first, substitute second'),
          tip('A negative rate means the quantity is shrinking, and saying so is part of the answer. "The top slides down at 0.75 m/s" and "dy/dt = −0.75" are the same statement, and a problem that asked in words wants the words.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'conic', conic: 'circle', a: 5, min: -6, max: 6, label: 'x² + y² = 25' },
            'The circle fails the vertical line test everywhere except at its two ends, so it is not a function and cannot be differentiated as one. It has a perfectly good tangent at every point regardless, and dy/dx = −x/y gives all of them.',
          ),
          figure(
            { kind: 'shape', shape: 'triangle', labels: { base: 'x — bottom slides out at 1 m/s', height: 'y — top slides down at ? m/s' } },
            'The ladder problem. The wall and the ground are fixed at a right angle and the ladder’s length never changes, so x² + y² = 100 holds at every instant — which is what makes it differentiable with respect to time.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A 10 m ladder leans against a wall. Its foot is pulled away from the wall at 1 m/s. How fast is the top sliding down when the foot is 6 m from the wall?'),
          steps([
            { label: 'Relate the quantities — Pythagoras, true at every instant.', latex: 'x^{2} + y^{2} = 100' },
            { label: 'Differentiate with respect to t.', latex: '2x\\frac{dx}{dt} + 2y\\frac{dy}{dt} = 0', note: 'The 100 is constant, so the right side is 0.' },
            { label: 'Find y at this instant.', latex: 'y = \\sqrt{100 - 36} = 8' },
            { label: 'Substitute everything known.', latex: '2(6)(1) + 2(8)\\frac{dy}{dt} = 0' },
            { label: 'Solve.', latex: '\\frac{dy}{dt} = -\\frac{12}{16} = -0.75' },
            { label: 'Answer in words.', latex: '\\text{the top slides down at } 0.75 \\text{ m/s}' },
          ]),
          insight('The minus sign is the whole of the physics. y is a height and dy/dt is negative, so the height is decreasing — the ladder is falling, which is what a ladder whose foot is pulled away does. An answer of +0.75 has the ladder climbing the wall on its own.'),
          tip('The two rates are not equal and never were, which is the point of the question. The foot moves at a steady 1 m/s and the top does not: as the ladder flattens, the top accelerates, and at x = 8 it is already falling faster than the foot is moving.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('implicit-differentiation', 4)] },
      independent: { title: 'On your own', blocks: [practice('implicit-differentiation', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('implicit-differentiation', 3, 'Implicit slopes, and rates that depend on other rates.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Differentiating y with respect to x leaves a dy/dx behind — it is the chain rule.',
            'Differentiate both sides, then rearrange to make dy/dx the subject.',
            'An implicit derivative may contain both x and y, and that is correct.',
            'Related rates is the same move with t as the independent variable.',
            'Differentiate first, then substitute the instant — never the other way round.',
            'The sign of a rate says growing or shrinking, and belongs in the answer.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'derivative-applications-01',
    unitId: 'unit-derivative-applications',
    conceptId: 'derivative-applications',
    title: 'Shape from the Derivative',
    summary: 'Where a curve rises, where it turns, and how to sketch one you have never seen.',
    objectives: [
      'Find the critical points of a function and classify them',
      'Use the sign of f′ to say where a function increases and decreases',
      'Use f″ for concavity, and sketch a curve from its derivatives',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Describing a curve without plotting it',
        blocks: [
          text('Sketch y = x³ − 6x² + 9x + 1.'),
          text('Plotting points is a poor method and always was. Twenty points might miss the interesting part entirely, and they never prove there is nothing else happening between two of them.'),
          insight('The derivative answers the questions a sketch is actually made of. Where is the curve rising? Where does it turn? How many turns are there at all? None of these needs a single plotted point, and together they determine the shape.'),
        ],
      },
      intuition: {
        title: 'The sign of the slope, and then the sign of its slope',
        blocks: [
          text('A slope is positive when a curve rises and negative when it falls, so the derivative reports the direction of travel:'),
          table(['Where', 'The curve'], [
            ['f′(x) > 0', 'is increasing — rising left to right'],
            ['f′(x) < 0', 'is decreasing'],
            ['f′(x) = 0', 'is momentarily flat — a stationary point'],
          ]),
          text('The values of x where f′(x) = 0 are the **critical points**. They are the only places a smooth curve can turn, which is what makes them worth finding: solve one equation and every candidate is in hand.'),
          warning('A critical point need not be a maximum or a minimum. y = x³ has f′(0) = 0 and does not turn there at all — it flattens and carries on climbing. "f′(c) = 0" means "candidate", never "extremum", and reporting one without testing it is the standard way to answer this kind of question wrongly.', 'Flat is not the same as turning'),
          text('**The first derivative test** settles which it is, by looking at the sign either side:'),
          table(['f′ changes', 'At that point'], [
            ['+ to −', 'a local maximum — rising, then falling'],
            ['− to +', 'a local minimum'],
            ['no change', 'neither — a flat spot, like x³ at 0'],
          ]),
          text('**The second derivative.** Differentiate f′ and you have the rate at which the slope itself is changing, which is the curve’s bend:'),
          math("f''(x) > 0 \\;\\Rightarrow\\; \\text{concave up}, \\qquad f''(x) < 0 \\;\\Rightarrow\\; \\text{concave down}"),
          insight('That gives a faster test. At a critical point, concave up means the curve is sitting in a valley — a minimum; concave down means it is sitting on a hill — a maximum. Where f″(c) = 0 the test is silent and the sign test must be used instead.'),
          text('A point where f″ changes sign is an **inflection point**: the bend reverses, from cupped upwards to cupped downwards or back. The curve is still rising or falling as it was; it is the *curvature* that flips.'),
          warning('Concavity and direction are independent, and confusing them is easy. A curve can be falling while concave up — slowing its fall — and rising while concave down. f′ says which way; f″ says whether that is intensifying or easing off.', 'Rising and bending are different questions'),
          warning('A local maximum is a *height*, and the x that produces it is not it. If f′(1) = 0 and the test says maximum, the answer to "what is the local maximum" is f(1), not 1. Substituting back is the last step and the one most often skipped.', 'The maximum is a y-value'),
          text('The sketch is then assembled from answers rather than points:'),
          list([
            'Find f′ and solve f′(x) = 0 for the critical points.',
            'Classify each with the sign test or with f″.',
            'Find the y-value at each one — those are the turning points.',
            'Read off the intervals of increase and decrease between them.',
            'Add the y-intercept, and the ends: what does f do as x grows large either way?',
          ], true),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              roots: [-2, 0, 2],
              points: [
                { x: -1.15, y: 3.08, label: 'local max' },
                { x: 1.15, y: -3.08, label: 'local min' },
              ],
              min: -4,
              max: 4,
              label: 'y = x³ − 4x',
            },
            'Two turning points, and the derivative found both. f′(x) = 3x² − 4 is zero at x ≈ ±1.15 — nowhere near the roots at −2, 0 and 2, which is why plotting the roots and joining them up gets the shape wrong.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'cubic',
              points: [{ x: 0, y: 0, label: 'f′ = 0, and no turn' }],
              min: -3,
              max: 3,
              label: 'y = x³',
            },
            'The warning, drawn. The tangent at the origin is horizontal, so x = 0 is a critical point — and the curve climbs straight through it. It is an inflection point: the bend reverses there, and the direction does not.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('For f(x) = x³ − 6x² + 9x + 1, find and classify the turning points, and say where the function increases.'),
          steps([
            { label: 'Differentiate.', latex: "f'(x) = 3x^{2} - 12x + 9" },
            { label: 'Factorise and solve f′ = 0.', latex: '3(x - 1)(x - 3) = 0 \\;\\Rightarrow\\; x = 1, \\; x = 3' },
            { label: 'Second derivative, for the test.', latex: "f''(x) = 6x - 12" },
            { label: 'Test x = 1.', latex: "f''(1) = -6 < 0 \\;\\Rightarrow\\; \\text{local maximum}" },
            { label: 'Test x = 3.', latex: "f''(3) = 6 > 0 \\;\\Rightarrow\\; \\text{local minimum}" },
            { label: 'Heights — substitute back into f, not f′.', latex: 'f(1) = 5, \\qquad f(3) = 1' },
            { label: 'Read off the intervals.', latex: '\\text{increasing for } x < 1 \\text{ and } x > 3' },
          ]),
          insight('The two turning points sit at heights 5 and 1, and the local minimum at (3, 1) is higher than plenty of the curve to its left. "Local" is doing real work in that name — it means lowest *nearby*, and this cubic falls below 1 well to the left of the origin and keeps going.'),
          tip('The classification could have been done without f″. Between 1 and 3 pick x = 2: f′(2) = 12 − 24 + 9 = −3, negative, so the curve falls between the two critical points — which makes the first a maximum and the second a minimum, with no second derivative computed.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('derivative-applications', 4)] },
      independent: { title: 'On your own', blocks: [practice('derivative-applications', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('derivative-applications', 3, 'Classifying critical points, and reading shape off two derivatives.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'f′ > 0 rising, f′ < 0 falling, f′ = 0 stationary.',
            'Critical points are candidates for turning points, not turning points.',
            'First derivative test: + to − is a maximum, − to + a minimum, no change neither.',
            'f″ > 0 is concave up (a minimum sits in it); f″ < 0 is concave down.',
            'An inflection point is where f″ changes sign — the bend reverses, not the direction.',
            'The maximum value is f(c); the critical point is only where it happens.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'optimization-01',
    unitId: 'unit-derivative-applications',
    conceptId: 'optimization',
    title: 'Optimisation',
    summary: 'The largest volume, the cheapest box, the shortest path — found rather than guessed.',
    objectives: [
      'Turn a described situation into one function of one variable',
      'Use the derivative to find the optimum and confirm which kind it is',
      'Answer the question that was asked, in the units it was asked in',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'A hundred metres of fence',
        blocks: [
          text('You have 100 m of fencing and a straight river. You want to enclose a rectangular paddock using the river as one side, so only three sides need fence. What is the largest area you can enclose?'),
          text('Try a few. A pen 10 m deep is 80 m along the river: 800 m². A pen 40 m deep is 20 m along: 800 m² again. A pen 25 m deep is 50 m along: 1250 m².'),
          insight('Guessing found something the first two guesses would have hidden — the area is not monotonic, and there is a best shape somewhere in the middle. Guessing cannot prove 1250 is the best, and cannot find it at all when the numbers are less friendly. The derivative can do both.'),
        ],
      },
      intuition: {
        title: 'One quantity, one variable, one derivative',
        blocks: [
          text('Every optimisation problem is the same six steps, and five of them happen before any calculus:'),
          list([
            'Name the variables, and draw the thing if it can be drawn.',
            'Write the quantity to be optimised as an equation.',
            'Write the constraint — the fixed resource, the fixed perimeter, the fixed volume.',
            'Use the constraint to eliminate a variable, leaving one function of one variable.',
            'Differentiate, set to zero, solve.',
            'Confirm it is the kind of optimum wanted, then answer the question asked.',
          ], true),
          insight('Step 4 is the one that makes it calculus. The area of a rectangle depends on two variables and has no maximum at all — you can always make it bigger. It is the constraint that creates a best answer, by tying the two variables together and turning a surface into a curve.'),
          warning('The quantity being optimised and the constraint are different equations, and swapping them produces confident nonsense. The one you differentiate is the one you want to make large or small; the other is the one you substitute *into* it. Label them before starting.', 'Which equation gets differentiated'),
          text('**Confirming.** A stationary point is a candidate, exactly as in the last lesson. Confirm with f″, or with the sign of f′ either side, or — often quickest — by the shape of the function you ended up with:'),
          tip('An area that comes out as a downward parabola has exactly one stationary point and it is the maximum, with no test required. Say so rather than differentiating twice; it is a complete argument.'),
          warning('Check the domain, and check the ends. A length cannot be negative and cannot exceed the fence available, so x here lives in 0 < x < 50. On a closed interval the largest value can occur at an endpoint where the derivative is not zero at all, and a solution that only ever looked at critical points would miss it.', 'The ends are candidates too'),
          warning('Answer the question. The algebra usually ends by finding x, and the question usually asks for something else — the area, the cost, the dimensions. Handing back x = 25 when the question said "what is the largest area" is not a small slip: it answers a question nobody asked.', 'Reread the prompt before writing the answer'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            { kind: 'shape', shape: 'rectangle', labels: { width: 'y — along the river', height: 'x — depth' } },
            'Three sides fenced, and the river is the fourth. The fence used is 2x + y, which is fixed at 100; the area is xy, which is not fixed at all. Two equations, and only one of them is being maximised.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              transform: { a: -1, h: 2, k: 4 },
              points: [{ x: 2, y: 4, label: 'the optimum' }],
              min: -1,
              max: 6,
              label: 'area against depth',
            },
            'The shape of every one-constraint optimisation of this kind: a downward parabola, zero at both ends, with a single flat point in the middle. The two guesses that both gave 800 m² sit symmetrically either side of the peak, which is why they agreed.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('With 100 m of fence and a river along one side, find the dimensions of the largest rectangular paddock and state its area.'),
          steps([
            { label: 'Name the variables.', latex: 'x = \\text{depth}, \\quad y = \\text{length along the river}' },
            { label: 'The quantity to maximise.', latex: 'A = xy' },
            { label: 'The constraint — three sides only.', latex: '2x + y = 100' },
            { label: 'Eliminate y.', latex: 'y = 100 - 2x \\;\\Rightarrow\\; A(x) = x(100 - 2x) = 100x - 2x^{2}' },
            { label: 'Differentiate and set to zero.', latex: "A'(x) = 100 - 4x = 0" },
            { label: 'Solve.', latex: 'x = 25' },
            { label: 'Confirm it is a maximum.', latex: "A''(x) = -4 < 0", note: 'Or: A is a downward parabola, so its one stationary point is the peak.' },
            { label: 'Recover the other dimension.', latex: 'y = 100 - 50 = 50' },
            { label: 'Answer what was asked.', latex: '25 \\text{ m} \\times 50 \\text{ m}, \\quad A = 1250 \\text{ m}^{2}' },
          ]),
          insight('The answer has a shape worth noticing: the side along the river is exactly twice the depth, and the fence is split evenly — 50 m on the two depths together, 50 m along the river. That is true for every problem of this form, whatever the length of fence, and it is the kind of pattern that only shows up once the general method has replaced guessing.'),
          warning('x = 25 was reached at step 6, three steps before the answer. A solution that stops there has done all the calculus and none of the question.', 'The calculus finishes before the problem does'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('optimization', 4)] },
      independent: { title: 'On your own', blocks: [practice('optimization', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('optimization', 3, 'Constraints, domains and the question actually asked.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Write two equations: the quantity to optimise, and the constraint.',
            'Use the constraint to get down to one variable — that is what creates a best answer.',
            'Differentiate the quantity, not the constraint.',
            'Confirm the critical point is the maximum or minimum you wanted.',
            'Check the domain; on a closed interval an endpoint can beat every critical point.',
            'Finish by answering the question, in its own units.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'motion-derivatives-01',
    unitId: 'unit-derivative-applications',
    conceptId: 'motion-derivatives',
    title: 'Velocity and Acceleration',
    summary: 'Position, its rate of change, and the rate of change of that.',
    objectives: [
      'Find velocity and acceleration by differentiating a position function',
      'Say when a particle is at rest, and which way it is moving',
      'Distinguish speeding up from slowing down using the signs of v and a',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'The speedometer, and the pedal',
        blocks: [
          text('A position function s(t) says where something is at time t. Rates of change gave you the first thing to do with it: differentiate, and you have how fast the position is changing — the velocity.'),
          text('There is no reason to stop. Velocity is itself a function of time, and differentiating it gives how fast the *velocity* is changing, which is the acceleration.'),
          math("v(t) = s'(t), \\qquad a(t) = v'(t) = s''(t)"),
          insight('Three functions, two differentiations, and one physical meaning each: where it is, how fast that is changing, how fast that is changing. The second derivative was an abstraction in the last lesson — concavity — and here it is something you can feel through the seat of a car.'),
        ],
      },
      intuition: {
        title: 'Signs carry the physics',
        blocks: [
          text('**Velocity is signed; speed is not.** Velocity −5 m/s and velocity +5 m/s are different motions at the same speed:'),
          math('\\text{speed} = |v(t)|'),
          warning('A question asking for speed wants the size and a question asking for velocity wants the sign, and the sign is the direction of travel. Reporting −5 m/s as a speed, or 5 m/s as a velocity when the particle is moving left, answers a different question.', 'Velocity has a direction; speed does not'),
          text('**At rest** means the velocity is zero. Those are the instants the particle stops — and if it changes direction at all, it does so there:'),
          math('v(t) = 0 \\;\\Rightarrow\\; \\text{momentarily at rest}'),
          insight('This is the critical-point machinery from two lessons ago, wearing physics clothes. Solving v(t) = 0 is solving s′(t) = 0, so the times a particle is at rest are exactly the stationary points of its position graph — and a sign change in v is a turning point, which is the particle turning round.'),
          text('**Speeding up or slowing down** is not read from the acceleration alone. It is read from the two signs together:'),
          table(['v', 'a', 'The motion'], [
            ['positive', 'positive', 'moving right, speeding up'],
            ['positive', 'negative', 'moving right, slowing down'],
            ['negative', 'negative', 'moving left, speeding up'],
            ['negative', 'positive', 'moving left, slowing down'],
          ]),
          insight('The rule underneath the table is one sentence: same signs speed up, opposite signs slow down. Acceleration is not "getting faster" — it is a push, and a push against the direction of travel is a brake.'),
          warning('At the top of a thrown ball’s flight the velocity is zero and the acceleration is not. Gravity is still pulling at every instant, including that one; if the acceleration were zero there the ball would hang in the air. "At rest" is a statement about v only.', 'Zero velocity does not mean zero acceleration'),
          warning('Displacement and distance travelled differ whenever the object turns round. A particle that goes 5 m right and 5 m back has displacement 0 and has travelled 10 m. To get distance, find the turning times, and add the lengths of the legs separately.', 'Where it ended up is not how far it went'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              roots: [0, 3, 3],
              points: [
                { x: 1, y: 4, label: 't = 1, at rest' },
                { x: 3, y: 0, label: 't = 3, at rest' },
              ],
              min: -1,
              max: 5,
              label: 's(t) = t³ − 6t² + 9t',
            },
            'Position against time, not a picture of the path. The two marked points are where the graph is flat — the two instants the particle is at rest — and between them the graph falls, which is the particle moving backwards.',
          ),
          tip('Reading a position-time graph as a map of the journey is the standard confusion, and it is worth saying out loud: the particle here moves along a line, and this curve is a record of where it was, not of where it went.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A particle moves on a line with s(t) = t³ − 6t² + 9t metres, t in seconds, t ≥ 0. Find when it is at rest, and describe its motion at t = 2.'),
          steps([
            { label: 'Differentiate for velocity.', latex: 'v(t) = 3t^{2} - 12t + 9' },
            { label: 'Factorise and solve v = 0.', latex: '3(t - 1)(t - 3) = 0 \\;\\Rightarrow\\; t = 1, \\; t = 3' },
            { label: 'Differentiate again for acceleration.', latex: 'a(t) = 6t - 12' },
            { label: 'Velocity at t = 2.', latex: 'v(2) = 12 - 24 + 9 = -3', note: 'Negative: moving in the negative direction.' },
            { label: 'Acceleration at t = 2.', latex: 'a(2) = 12 - 12 = 0' },
            { label: 'Read the two signs.', latex: 'v < 0, \\; a = 0' },
          ]),
          insight('At t = 2 the particle is moving left at 3 m/s and, for that instant only, is neither speeding up nor slowing down. It is the moment the braking stops and the pushing starts: a(t) changes sign at t = 2, which makes it the inflection point of the position graph and the instant of greatest speed on the backward leg.'),
          tip('The distance travelled in the first three seconds is not s(3) − s(0) = 0. The particle went from 0 out to s(1) = 4, came back to s(3) = 0, and so travelled 4 + 4 = 8 metres while ending exactly where it began.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('motion-derivatives', 4)] },
      independent: { title: 'On your own', blocks: [practice('motion-derivatives', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('motion-derivatives', 3, 'Rest, direction, and speeding up against slowing down.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'v = s′ and a = v′ = s″.',
            'Velocity is signed; speed is its size.',
            'At rest means v = 0 — and that is where the particle can turn round.',
            'Same signs on v and a means speeding up; opposite signs means slowing down.',
            'Zero velocity does not imply zero acceleration.',
            'Distance travelled counts every leg; displacement only counts the ends.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'antiderivatives-01',
    unitId: 'unit-integrals',
    conceptId: 'antiderivatives',
    title: 'Antiderivatives',
    summary: 'Running differentiation backwards, and the constant that can never be recovered.',
    objectives: [
      'Find the antiderivative of a polynomial using the reverse power rule',
      'Explain why every antiderivative carries a + C',
      'Use an initial condition to pin down which antiderivative is meant',
    ],
    estimatedMinutes: 40,
    sections: {
      introduction: {
        title: 'Going the other way',
        blocks: [
          text('Given position, you can find velocity. The reverse ought to be possible too: a car with a known speed at every instant, and a known starting point, has been somewhere definite the whole time.'),
          text('So: which function has derivative 2x?'),
          text('x², clearly. Also x² + 1, and x² − 7, and x² + 4000 — every one of them differentiates to 2x, because the constant contributes nothing.'),
          insight('That is not a flaw in the question. Differentiation genuinely destroys information — the height a curve sits at — and no reversal can invent it back. Every antiderivative is a whole family of curves, identical in shape, stacked at every possible height.'),
        ],
      },
      intuition: {
        title: 'The power rule, backwards',
        blocks: [
          text('F is an **antiderivative** of f when F′ = f. To find one, undo each step of the power rule: the power went down by one, so put it back up; it was multiplied by the old power, so divide by the new one:'),
          math('\\int x^{n}\\,dx = \\frac{x^{n+1}}{n + 1} + C \\qquad (n \\neq -1)'),
          tip('Check any antiderivative by differentiating it. That is not a safety net — it is the definition, and it makes this the one topic in mathematics where you can always verify your own answer completely.'),
          warning('n = −1 is excluded, and the formula shows why: it would put a zero in the denominator. The antiderivative of 1/x is a genuinely different kind of function — the natural logarithm — and it is the one gap in this rule.', 'The one power the rule cannot do'),
          text('The **+ C** is not decoration. It stands for the lost constant, and dropping it claims one member of the family is the answer when the question has infinitely many:'),
          math('\\int 2x\\,dx = x^{2} + C'),
          warning('Omitting + C is the standard error here, and it is a claim rather than a slip — it says the antiderivative is unique, which is exactly the thing that is false. Write it as you write the answer, not afterwards.', 'The constant is part of the answer'),
          text('Sums and constant multiples come apart exactly as they do for derivatives:'),
          math('\\int \\big(af(x) + bg(x)\\big)\\,dx = a\\!\\int\\! f(x)\\,dx + b\\!\\int\\! g(x)\\,dx'),
          warning('Products and quotients do not. There is no rule that antidifferentiates u·v by antidifferentiating each and multiplying — the product rule has no clean reverse at this level. When a product appears, multiply it out first; if it will not multiply out, it is beyond this course.', 'No product rule in reverse'),
          text('**Pinning the constant.** One extra fact about the function — where it passes through — picks out one member of the family:'),
          insight('This is why the reverse question is answerable after all. Velocity alone cannot say where a car is, and velocity plus a starting position can. The + C is the starting position, and the initial condition is what supplies it.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'linear',
              transform: { a: 2 },
              lines: [
                { slope: 2, intercept: 3, label: '2x + 3', dashed: true },
                { slope: 2, intercept: -3, label: '2x − 3', dashed: true },
              ],
              min: -5,
              max: 5,
              label: '2x + C',
            },
            'Three of the infinitely many antiderivatives of f(x) = 2. Identical slope everywhere, different heights — and the derivative cannot tell them apart, because slope is all it measures. Choosing between them needs one point the curve is known to pass through.',
          ),
          tip('The picture is the whole argument for + C. Erase the labels and there is no measurement of slope, at any x, that distinguishes these three lines.'),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('A function has f′(x) = 6x² − 4x + 5 and passes through (1, 10). Find f(x).'),
          steps([
            { label: 'Antidifferentiate term by term.', latex: '\\int (6x^{2} - 4x + 5)\\,dx' },
            { label: 'Raise each power and divide by the new one.', latex: '\\frac{6x^{3}}{3} - \\frac{4x^{2}}{2} + 5x + C' },
            { label: 'Simplify.', latex: 'f(x) = 2x^{3} - 2x^{2} + 5x + C' },
            { label: 'Use the point: substitute x = 1, f = 10.', latex: '2 - 2 + 5 + C = 10' },
            { label: 'Solve for C.', latex: 'C = 5' },
            { label: 'State the one function that fits.', latex: 'f(x) = 2x^{3} - 2x^{2} + 5x + 5' },
          ]),
          insight('Two different jobs, done in order. Steps 1 to 3 answer "which family", and steps 4 to 6 answer "which member". Skipping the + C makes the second half impossible, because there would be nothing left to solve for.'),
          tip('Verify by differentiating the answer: 6x² − 4x + 5, which is what was given. That check is available on every problem in this lesson and costs ten seconds.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('antiderivatives', 4)] },
      independent: { title: 'On your own', blocks: [practice('antiderivatives', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('antiderivatives', 3, 'Reversing the power rule, and initial conditions.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'F is an antiderivative of f when F′ = f — so check by differentiating.',
            '∫xⁿ dx = xⁿ⁺¹/(n + 1) + C, for every n except −1.',
            'The + C is the information differentiation destroyed; it is part of the answer.',
            'Sums and constant multiples split; products and quotients do not.',
            'One point on the curve pins C and picks out a single function.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'integrals-01',
    unitId: 'unit-integrals',
    conceptId: 'integrals',
    title: 'Area Under a Curve',
    summary: 'Adding up rectangles that get thinner forever, and landing on an exact area.',
    objectives: [
      'Approximate an area with left- and right-endpoint rectangles',
      'State the definite integral as the limit of those sums',
      'Read a definite integral as a signed area',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'An area with no formula',
        blocks: [
          text('What is the area between y = x², the x-axis, and the vertical lines x = 0 and x = 2?'),
          text('Geometry has nothing to offer. There is no formula for the area of a region with a curved side, and every area formula you know — rectangle, triangle, circle — was either given to you or derived by a trick specific to that shape.'),
          insight('There is a general method, and it is the same idea as the limit, used a third time. Cover the region with shapes you *can* measure, accept that the answer is wrong, and then make it less wrong without limit.'),
        ],
      },
      intuition: {
        title: 'Rectangles, and then thinner rectangles',
        blocks: [
          text('Chop the interval from a to b into n equal strips, each of width'),
          math('\\Delta x = \\frac{b - a}{n}'),
          text('and stand a rectangle on each one, its height taken from the curve at the strip’s left edge. The total is a sum you can compute:'),
          math('\\sum_{i=0}^{n-1} f(x_{i})\\,\\Delta x'),
          insight('For a rising curve those rectangles all sit below it, so the sum is an underestimate. Take the heights from the right edges instead and every rectangle pokes above, giving an overestimate. The true area is trapped between the two, and squeezing them together is the entire method.'),
          text('More strips means less overshoot on each one, and the gap between the two estimates closes. The **definite integral** is what the sums approach:'),
          math('\\int_{a}^{b} f(x)\\,dx = \\lim_{n \\to \\infty} \\sum_{i=0}^{n-1} f(x_{i})\\,\\Delta x'),
          text('The notation is a picture of that sentence. ∫ is a stretched letter S, for sum; f(x) is the height of a rectangle; dx is its width, shrunk to nothing. It was designed to be read.'),
          warning('A definite integral is a number, not a function, and it carries no + C. The constant that mattered so much in the last lesson is a feature of the *indefinite* integral — the family of antiderivatives — and an area is one specific quantity with one specific value.', 'Definite and indefinite are different objects'),
          text('**Signed area.** The heights f(xᵢ) are signed, so a strip below the axis contributes a negative amount:'),
          warning('An integral that comes out negative has not gone wrong. It is reporting more area below the axis than above. If a question asks for *area* rather than for the integral, split the interval at every root and add the sizes — an integral of zero over a symmetric wave is correct and is not an area of zero.', 'Integral and area are not synonyms'),
          tip('The exact value for x² from 0 to 2 can be got this way with no calculus at all, using the sum of the first n squares — n(n + 1)(2n + 1)/6 — and letting n grow. It comes to 8/3. Doing it once is worth the hour; doing it a second time is why the next lesson exists.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              shade: { from: 0, to: 2, rectangles: 4 },
              min: -1,
              max: 5,
              label: 'y = x²',
            },
            'Four left-endpoint rectangles under y = x² from 0 to 2. Each one is short — its height is taken at the left edge, where the curve is lowest across the strip — so the total, 1.75, is an underestimate of the true area.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              shade: { from: 0, to: 2 },
              min: -1,
              max: 5,
              label: 'y = x²',
            },
            'The region itself: what the rectangles are approximating, and what the integral is defined to be. Its exact area is 8/3 ≈ 2.667, between the left-rectangle underestimate of 1.75 and the right-rectangle overestimate of 3.75.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Estimate the area under y = x² from x = 0 to x = 2 using four left-endpoint rectangles, and say whether it is too big or too small.'),
          steps([
            { label: 'Find the strip width.', latex: '\\Delta x = \\frac{2 - 0}{4} = 0.5' },
            { label: 'List the left edges.', latex: 'x = 0, \\; 0.5, \\; 1, \\; 1.5' },
            { label: 'Height of each rectangle, from f(x) = x².', latex: '0, \\; 0.25, \\; 1, \\; 2.25' },
            { label: 'Add the heights.', latex: '0 + 0.25 + 1 + 2.25 = 3.5' },
            { label: 'Multiply by the common width.', latex: '3.5 \\times 0.5 = 1.75' },
            { label: 'Judge the error.', latex: '\\text{an underestimate}', note: 'The curve rises, so each left-edge height is the lowest the curve gets on its strip.' },
          ]),
          insight('1.75 against a true value of 8/3 ≈ 2.667 is a 34% error, from four rectangles. With forty it drops to about 4%, and with four hundred to 0.4% — converging, and slowly. That slowness is exactly why nobody computes areas this way in practice, and why the theorem in the next lesson is one of the most useful facts in mathematics.'),
          tip('Multiply by Δx once at the end rather than on every rectangle. Every strip has the same width, so it factors out of the sum — which is what turns a page of arithmetic into two lines.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('integrals', 4)] },
      independent: { title: 'On your own', blocks: [practice('integrals', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('integrals', 3, 'Rectangle sums, over- and underestimates, and signed area.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'Approximate an area with n rectangles of width Δx = (b − a)/n.',
            'Left endpoints underestimate a rising curve; right endpoints overestimate it.',
            'The definite integral is the limit of those sums as n → ∞.',
            '∫ is a stretched S for sum; f(x) is a height and dx a width.',
            'A definite integral is a number and carries no + C.',
            'Area below the axis counts negative, so an integral is a signed area.',
          ]),
        ],
      },
    },
  }),

  lesson({
    id: 'fundamental-theorem-01',
    unitId: 'unit-integrals',
    conceptId: 'fundamental-theorem',
    title: 'The Fundamental Theorem of Calculus',
    summary: 'Why undoing a derivative gives an area — which is not obvious, and was not obvious for two thousand years.',
    objectives: [
      'Evaluate a definite integral using an antiderivative',
      'State why the area function’s derivative is the original function',
      'Find the area between two curves',
    ],
    estimatedMinutes: 45,
    sections: {
      introduction: {
        title: 'Two subjects that had no reason to meet',
        blocks: [
          text('This course has built two things, and they have nothing to do with each other.'),
          text('One is the derivative: slopes, tangents, rates. The other is the integral: areas, built out of rectangles by a limit. They come from different questions — one from a moving body, one from a plot of land — and were studied separately for two thousand years.'),
          insight('They are the same question. Undoing a derivative computes an area, exactly, in one line. That connection is the **fundamental theorem of calculus**, it is the reason the subject is called calculus rather than two subjects with two names, and it is genuinely surprising — it should be read as news rather than as a definition.'),
        ],
      },
      intuition: {
        title: 'The area function, and its derivative',
        blocks: [
          text('Fix the left end and let the right end move. Define A(x) to be the area under the curve from a out to x — a function, because the area grows as x does:'),
          math('A(x) = \\int_{a}^{x} f(t)\\,dt'),
          text('Now ask how fast A grows. Push the right edge out by a tiny amount h. The new area is a sliver: almost a rectangle, of width h and height f(x).'),
          math('A(x + h) - A(x) \\approx f(x) \\cdot h \\;\\Longrightarrow\\; \\frac{A(x + h) - A(x)}{h} \\approx f(x)'),
          insight('The left-hand side is the difference quotient for A. Let h shrink and the approximation becomes exact, because the sliver becomes indistinguishable from a rectangle. So A′(x) = f(x) — **the area function is an antiderivative of the curve**. That is the theorem, and the proof is the sliver.'),
          text('The practical half follows immediately. If F is any antiderivative of f, then F and A differ by a constant, and subtracting kills it:'),
          math('\\int_{a}^{b} f(x)\\,dx = F(b) - F(a)'),
          text('Written out, with the standard bracket notation for the evaluation:'),
          math('\\int_{a}^{b} f(x)\\,dx = \\Big[F(x)\\Big]_{a}^{b} = F(b) - F(a)'),
          insight('This is why a definite integral needs no + C. Whichever antiderivative you pick, the constant appears in both F(b) and F(a) and cancels in the subtraction — so the answer does not depend on the choice, and there is nothing to pin down.'),
          warning('Top minus bottom, in that order. Reversing the subtraction negates the answer, and unlike a sign error in arithmetic it produces a plausible-looking number with no warning attached.', 'Which way round'),
          warning('The theorem needs f continuous on [a, b]. An integral straight through a vertical asymptote — ∫ from −1 to 1 of 1/x², say — is not covered, and evaluating it blindly gives a negative number for a region lying entirely above the axis. When an integrand blows up inside the interval, the answer the formula produces is not an area at all.', 'Check for a break inside the interval'),
          text('**Area between two curves.** Once one area is available, the region between two curves is a subtraction, done inside the integral:'),
          math('\\text{area} = \\int_{a}^{b} \\big(\\text{top} - \\text{bottom}\\big)\\,dx'),
          tip('Integrating the difference handles the axis automatically. Neither curve needs to be above the axis, and no case-splitting is required — only knowing which of the two is on top, which is what the intersection points are found for.'),
          text('**Net change.** The same theorem read in the language of rates says something a physicist uses daily: integrating a rate of change over an interval gives the total change over it.'),
          math('\\int_{t_{1}}^{t_{2}} v(t)\\,dt = s(t_{2}) - s(t_{1})'),
          insight('Which closes the loop the motion lesson opened. Differentiating position gave velocity; integrating velocity gives back the change in position. The two operations are inverse, and the fundamental theorem is the statement that they are.'),
        ],
      },
      visual: {
        title: 'Seeing it',
        blocks: [
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              shade: { from: 0, to: 1.5 },
              points: [{ x: 1.5, y: 2.25, label: 'x — the moving edge' }],
              min: -1,
              max: 5,
              label: 'A(x)',
            },
            'The area function. The left edge is fixed and the right edge is x, so the shaded amount is a function of x — and pushing that edge a hair to the right adds a sliver of width h and height f(x). That sliver is the whole proof: the rate at which the area grows is the height of the curve.',
          ),
          figure(
            {
              kind: 'function-graph',
              curve: 'quadratic',
              lines: [{ slope: 1, intercept: 2, label: 'y = x + 2' }],
              points: [
                { x: -1, y: 1, label: 'x = −1' },
                { x: 2, y: 4, label: 'x = 2' },
              ],
              min: -3,
              max: 5,
              label: 'y = x²',
            },
            'Area between two curves. The line is above the parabola between the two intersection points, so the region is ∫ from −1 to 2 of (x + 2 − x²) dx — the top curve minus the bottom one, integrated between the crossings.',
          ),
        ],
      },
      'worked-example': {
        title: 'Worked example',
        blocks: [
          text('Evaluate the integral of 3x² − 2x from x = 1 to x = 3.'),
          steps([
            { label: 'Write the integral.', latex: '\\int_{1}^{3} (3x^{2} - 2x)\\,dx' },
            { label: 'Find an antiderivative — any one, so take C = 0.', latex: 'F(x) = x^{3} - x^{2}' },
            { label: 'Set up the evaluation bracket.', latex: '\\Big[x^{3} - x^{2}\\Big]_{1}^{3}' },
            { label: 'Substitute the top limit.', latex: 'F(3) = 27 - 9 = 18' },
            { label: 'Substitute the bottom limit.', latex: 'F(1) = 1 - 1 = 0' },
            { label: 'Subtract, top first.', latex: '18 - 0 = 18' },
          ]),
          insight('Compare that with the previous lesson. Six lines, exact, and it would have been six lines for any limits and any polynomial — where the rectangle method gave 34% error from four rectangles and needed a summation formula to do better. This is what the theorem buys, and it is why it is worth its name.'),
          tip('Check the plausibility before moving on. The curve 3x² − 2x runs from 1 at x = 1 up to 21 at x = 3 over an interval of width 2, so an area somewhere near the middle height times the width — very roughly 2 × 10 — is expected. 18 fits; 1.8 or 180 would not.'),
        ],
      },
      guided: { title: 'Try these with support', blocks: [practice('fundamental-theorem', 4)] },
      independent: { title: 'On your own', blocks: [practice('fundamental-theorem', 5)] },
      challenge: {
        title: 'Something harder',
        blocks: [practice('fundamental-theorem', 3, 'Evaluations, areas between curves, and net change.')],
      },
      review: {
        title: 'What to remember',
        blocks: [
          list([
            'The area function A(x) = ∫ₐˣ f satisfies A′(x) = f(x) — the sliver argument.',
            'So ∫ₐᵇ f(x) dx = F(b) − F(a) for any antiderivative F.',
            'The + C cancels in the subtraction, which is why a definite integral has none.',
            'Top limit minus bottom limit, in that order.',
            'The theorem needs f continuous across the whole interval.',
            'Area between curves is ∫(top − bottom); integrating a rate gives the net change.',
          ]),
        ],
      },
    },
  }),
];
